import type { RoomKind } from '../realtime/frames';
import { parseRoomKind } from '../realtime/frames';

/**
 * Client of the authenticated room surface (docs/contract.md, CR-14): POST /api/v1/rooms creates
 * a room (the web quick-session button, the dashboard's custom-address form and the calendar's
 * room pre-registration) and GET /api/v1/rooms/{room}/availability is the public slug check the
 * create form calls while the user types. Same-origin calls: the Vite dev server proxies /api to
 * the control service, production serves the SPA same-origin.
 */

/** Body of POST /api/v1/rooms. Every field is optional server-side; defaults are server-owned. */
export interface CreateRoomBody {
  /**
   * Custom room address. Absent or empty makes the server generate one (the quick-session path);
   * present it is the user's arbitrary name (CR-26) - spaces, mixed case, Cyrillic - which the
   * server slugifies into the registered slug (the web sends the already-slugified value; a name
   * transliterating below three characters would answer 400).
   */
  readonly slug?: string;
  /** Room kind, written once at creation; defaults to meeting. */
  readonly kind?: RoomKind;
  /** The per-room lobby option (CR-14): participants wait for a staff admit. Default false. */
  readonly lobby?: boolean;
  /**
   * The room lifecycle deadline override (CR-26): absent defaults the room to 24h, `0` means the
   * room never expires, any other value clamps server-side into 60..43200 minutes (1h..30d).
   */
  readonly ttlMinutes?: number;
}

/** The 201 response of POST /api/v1/rooms; `name` carries the slug on the wire. */
export interface CreatedRoom {
  readonly name: string;
  readonly kind: RoomKind;
  readonly lobbyRequired: boolean;
  /**
   * The room's lifecycle deadline (CR-26): RFC 3339, or null when the room never expires
   * (ttlMinutes 0). The toast of a successful create surfaces it.
   */
  readonly expiresAt: string | null;
}

/** The 200 response of GET /api/v1/rooms/{room}/availability. */
export interface SlugAvailability {
  readonly available: boolean;
  /**
   * The registered slug the arbitrary path name slugified to (CR-26): the form fills the field
   * with it, so «Моя Встреча» ends up as moya-vstrecha in the input.
   */
  readonly slug: string;
}

/** Failure of the rooms endpoints: HTTP error, network failure or malformed response body. */
export class RoomApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'RoomApiError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrows an unknown create response to {@link CreatedRoom}. `name` (the slug) is required;
 * kind clamps through parseRoomKind, lobbyRequired clamps to false and expiresAt (CR-26) clamps
 * to null - the same lenient defaults the join response parser applies to its optional fields.
 */
export function parseCreatedRoom(body: unknown): CreatedRoom {
  if (!isRecord(body)) {
    throw new TypeError('create room response must be a JSON object');
  }
  if (typeof body.name !== 'string' || body.name.length === 0) {
    throw new TypeError('create room response field "name" must be a non-empty string');
  }
  return {
    name: body.name,
    kind: parseRoomKind(body.kind),
    lobbyRequired: body.lobbyRequired === true,
    expiresAt: typeof body.expiresAt === 'string' && body.expiresAt.length > 0 ? body.expiresAt : null,
  };
}

/** Builds the RoomApiError for a non-2xx response, reading the contract error body when present. */
async function errorFromResponse(response: Response, endpoint: string): Promise<RoomApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body, keep the generic message.
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new RoomApiError(response.status, code, message);
}

/**
 * Creates a room as the signed-in Keycloak identity, which becomes the room owner. Guests cannot
 * create (401). A taken slug answers 409 conflict - creation is not an ensure; the form surfaces
 * it through the live availability check and the dialog's error handling.
 */
export async function createRoom(
  token: string,
  body: CreateRoomBody,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CreatedRoom> {
  let response: Response;
  try {
    response = await fetchImpl('/api/v1/rooms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    throw new RoomApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'create room');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new RoomApiError(response.status, 'invalid_response', 'create room response is not JSON');
  }
  try {
    return parseCreatedRoom(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed create room response';
    throw new RoomApiError(response.status, 'invalid_response', detail);
  }
}

/** The slug rule the create form enforces before querying availability (rooms.go validateRoomName). */
export const ROOM_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

/** True when the candidate satisfies the slug shape; the form gates the availability query on it. */
export function isValidCreateSlug(candidate: string): boolean {
  return ROOM_SLUG_PATTERN.test(candidate);
}

/**
 * Public slug check behind the create form's live availability line. No auth by design (the
 * contract's CR-14 section); the server slugifies the arbitrary name first (CR-26) and echoes
 * the registered slug it looked up. A name that transliterates below three characters answers
 * 400 and is surfaced as an error state by the caller.
 */
export async function fetchRoomAvailability(
  slug: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<SlugAvailability> {
  let response: Response;
  try {
    response = await fetchImpl(`/api/v1/rooms/${encodeURIComponent(slug)}/availability`);
  } catch {
    throw new RoomApiError(0, 'network_error', 'control service is unreachable');
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'availability');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new RoomApiError(
      response.status,
      'invalid_response',
      'availability response is not JSON',
    );
  }
  if (
    !isRecord(payload) ||
    typeof payload.available !== 'boolean' ||
    typeof payload.slug !== 'string' ||
    payload.slug.length === 0
  ) {
    throw new RoomApiError(
      response.status,
      'invalid_response',
      'availability response fields "available"/"slug" are missing or malformed',
    );
  }
  return { available: payload.available, slug: payload.slug };
}
